import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus } from 'lucide-react';

interface AddMedicalUpdateModalProps {
  onClose: () => void;
  onAdd: (update: {
    type: 'Diagnosis' | 'Treatment' | 'Prescription' | 'Recovery';
    title: string;
    date: string;
    veterinarian: string;
    clinicName: string;
    description: string;
    medications?: string[];
    followUpDate?: string;
    status: 'Active' | 'Resolved' | 'Ongoing';
  }) => void;
}

export function AddMedicalUpdateModal({
  onClose,
  onAdd,
}: AddMedicalUpdateModalProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    type: 'Diagnosis' as
      | 'Diagnosis'
      | 'Treatment'
      | 'Prescription'
      | 'Recovery',
    title: '',
    date: '',
    veterinarian: '',
    clinicName: '',
    description: '',
    medications: '',
    followUpDate: '',
    status: 'Active' as 'Active' | 'Resolved' | 'Ongoing',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const medicationsArray = formData.medications
      .split('\n')
      .map((m) => m.trim())
      .filter((m) => m.length > 0);

    onAdd({
      ...formData,
      medications: medicationsArray.length > 0 ? medicationsArray : undefined,
      followUpDate: formData.followUpDate || undefined,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border dark:border-white/8">
        <div className="bg-gradient-to-r from-red-600 to-pink-600 dark:from-red-700 dark:to-pink-700 text-white p-6 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <h2
                className="text-2xl mb-1"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('features.healthRecords.addMedicalUpdateModal.title')}
              </h2>
              <p
                className="text-sm opacity-90"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.healthRecords.addMedicalUpdateModal.subtitle')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 min-h-0 overflow-auto p-6 bg-white dark:bg-[#16151D]"
        >
          <div className="space-y-4">
            {/* Type */}
            <div>
              <label
                className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.healthRecords.addMedicalUpdateModal.updateType')}{' '}
                <span className="text-red-500 dark:text-red-400">*</span>
              </label>
              <select
                required
                value={formData.type}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    type: e.target.value as
                      | 'Diagnosis'
                      | 'Treatment'
                      | 'Prescription'
                      | 'Recovery',
                  })
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <option value="Diagnosis">
                  🔍{' '}
                  {t('features.healthRecords.addMedicalUpdateModal.diagnosis')}
                </option>
                <option value="Treatment">
                  💊{' '}
                  {t('features.healthRecords.addMedicalUpdateModal.treatment')}
                </option>
                <option value="Prescription">
                  📋{' '}
                  {t(
                    'features.healthRecords.addMedicalUpdateModal.prescription'
                  )}
                </option>
                <option value="Recovery">
                  ❤️‍🩹{' '}
                  {t('features.healthRecords.addMedicalUpdateModal.recovery')}
                </option>
              </select>
            </div>

            {/* Title */}
            <div>
              <label
                className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.healthRecords.addMedicalUpdateModal.titleLabel')}{' '}
                <span className="text-red-500 dark:text-red-400">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40"
                style={{ fontFamily: 'Inter, sans-serif' }}
                placeholder={t(
                  'features.healthRecords.addMedicalUpdateModal.titlePlaceholder'
                )}
              />
            </div>

            {/* Date */}
            <div>
              <label
                className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.healthRecords.addMedicalUpdateModal.date')}{' '}
                <span className="text-red-500 dark:text-red-400">*</span>
              </label>
              <input
                type="date"
                required
                value={formData.date}
                onChange={(e) =>
                  setFormData({ ...formData, date: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                style={{ fontFamily: 'Inter, sans-serif' }}
              />
            </div>

            {/* Veterinarian & Clinic */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label
                  className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t(
                    'features.healthRecords.addMedicalUpdateModal.veterinarian'
                  )}{' '}
                  <span className="text-red-500 dark:text-red-400">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.veterinarian}
                  onChange={(e) =>
                    setFormData({ ...formData, veterinarian: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                  placeholder={t(
                    'features.healthRecords.addMedicalUpdateModal.vetPlaceholder'
                  )}
                />
              </div>

              <div>
                <label
                  className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('features.healthRecords.addMedicalUpdateModal.clinicName')}{' '}
                  <span className="text-red-500 dark:text-red-400">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.clinicName}
                  onChange={(e) =>
                    setFormData({ ...formData, clinicName: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                  placeholder={t(
                    'features.healthRecords.addMedicalUpdateModal.clinicPlaceholder'
                  )}
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label
                className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.healthRecords.addMedicalUpdateModal.description')}{' '}
                <span className="text-red-500 dark:text-red-400">*</span>
              </label>
              <textarea
                required
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40"
                style={{ fontFamily: 'Inter, sans-serif' }}
                placeholder={t(
                  'features.healthRecords.addMedicalUpdateModal.descriptionPlaceholder'
                )}
              />
            </div>

            {/* Medications */}
            <div>
              <label
                className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t(
                  'features.healthRecords.addMedicalUpdateModal.medicationsOptional'
                )}
              </label>
              <textarea
                value={formData.medications}
                onChange={(e) =>
                  setFormData({ ...formData, medications: e.target.value })
                }
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40"
                style={{ fontFamily: 'Inter, sans-serif' }}
                placeholder={t(
                  'features.healthRecords.addMedicalUpdateModal.medicationsPlaceholder'
                )}
              />
              <p
                className="text-xs text-gray-500 dark:text-white/50 mt-1"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t(
                  'features.healthRecords.addMedicalUpdateModal.medicationsHint'
                )}
              </p>
            </div>

            {/* Status & Follow-up */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label
                  className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('features.healthRecords.addMedicalUpdateModal.status')}{' '}
                  <span className="text-red-500 dark:text-red-400">*</span>
                </label>
                <select
                  required
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      status: e.target.value as
                        | 'Active'
                        | 'Ongoing'
                        | 'Resolved',
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <option value="Active">
                    {t('features.healthRecords.addMedicalUpdateModal.active')}
                  </option>
                  <option value="Ongoing">
                    {t('features.healthRecords.addMedicalUpdateModal.ongoing')}
                  </option>
                  <option value="Resolved">
                    {t('features.healthRecords.addMedicalUpdateModal.resolved')}
                  </option>
                </select>
              </div>

              <div>
                <label
                  className="block text-sm text-gray-700 dark:text-white/90 mb-2"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t(
                    'features.healthRecords.addMedicalUpdateModal.followUpOptional'
                  )}
                </label>
                <input
                  type="date"
                  value={formData.followUpDate}
                  onChange={(e) =>
                    setFormData({ ...formData, followUpDate: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                />
              </div>
            </div>

            {/* Info Notice */}
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 rounded-lg p-4">
              <p
                className="text-sm text-red-800 dark:text-red-200"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                🏥{' '}
                {t(
                  'features.healthRecords.addMedicalUpdateModal.medicalNotice'
                )}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-white/8">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-gray-900 dark:text-white"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              {t('features.healthRecords.addMedicalUpdateModal.cancel')}
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-gradient-to-r from-red-600 to-pink-600 dark:from-red-700 dark:to-pink-700 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              <Plus className="w-5 h-5" />
              {t('features.healthRecords.addMedicalUpdateModal.addUpdate')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
